import React from "react";
import { Camera, CameraOff } from "lucide-react";
import ConfirmationModal from "../ConfirmationModal.jsx";
import { consolidationRequest as api } from "../../api/consolidationApi.js";
import ConsolidationPopover from "./ConsolidationPopover.jsx";
import { SECONDARY } from "./Fields.jsx";
import { workDateLabel } from "../../utils/consolidationPresentation.js";

export default function DayControls({ day, timezone, rights, busy, run, onReset }) {
  const [confirmation, setConfirmation] = React.useState(null);
  const [error, setError] = React.useState("");
  const state = day.consolidation;
  if (!state) return null;
  const canReset = rights.write && rights.reopen && rights.dispatchReopen;
  const captured = state.captured_at ? new Intl.DateTimeFormat("fr-CA", {
    timeZone: timezone, dateStyle: "medium", timeStyle: "short",
  }).format(new Date(state.captured_at)) : "";
  const PhotoIcon = state.has_photo ? Camera : CameraOff;
  const confirm = ({ textPromptValue }) => run(async () => {
    setError("");
    try {
      await api(`/days/${day.date}/reset`, { method: "POST", body: {
        version: confirmation.version, reason: textPromptValue,
      } });
      setConfirmation(null);
      await onReset();
    } catch (failure) { setError(failure.message); throw failure; }
  });
  return <>
    <ConsolidationPopover label={state.has_photo ? "Photo du Dispatch conservée" : "Aucune photo du Dispatch prise"}
      tooltip={!state.has_photo ? <p className="text-xs">La « photo » est une copie du Dispatch conservée pour la consolidation. Elle sera créée automatiquement au premier enregistrement, une fois le Dispatch clos.</p> : null}
      icon={PhotoIcon} dismissible={!confirmation}
      className={`inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-[4px] border px-2 py-1 text-left text-xs font-semibold focus-visible:outline-2 focus-visible:outline-accent ${state.has_photo
        ? "border-accent bg-accent-soft text-accent" : "border-amber-400 bg-amber-50 text-amber-950"}`}>
      <p className="text-xs">{state.has_photo
        ? (captured ? <>Prise le <time dateTime={state.captured_at} className="font-semibold">{captured}</time></> : "Date de la photo non disponible.")
        : state.editable ? "Dispatch clos · La photo de toute la journée sera prise au premier enregistrement." : "Dispatch ouvert · La photo sera prise au premier enregistrement après la clôture."}</p>
      {canReset ? <details className="max-w-full text-xs text-slate-600">
        <summary className="cursor-pointer rounded-[4px] px-2 py-1 focus-visible:outline-accent">Actions avancées</summary>
        <div className="mt-1 grid max-w-sm gap-2 rounded-[4px] border border-slate-200 bg-white p-2">
          <p>Pour abandonner toute la consolidation de cette journée. Pour corriger un relevé, utilisez ses actions Modifier ou Vider les saisies.</p>
          <button type="button" className={`${SECONDARY} !border-red-200 !text-red-700 hover:!border-red-300 hover:!bg-red-50`}
            disabled={busy} onClick={() => { setError(""); setConfirmation({ version: state.reset_version, counts: state.counts }); }}>
            Recommencer toute la consolidation
          </button>
        </div>
      </details> : null}
    </ConsolidationPopover>
    {confirmation ? <ConfirmationModal title={`Recommencer toute la consolidation du ${workDateLabel(day.date)} ?`}
      description="Cette action archive les saisies de la journée, annule leurs validations et rouvre le Dispatch."
      tone="danger" cornerRadius="grain" contentDensity="compact" panelSize="narrow"
      summaryItems={[
        { label: "Relevés", value: String(confirmation.counts?.sheets || 0) },
        { label: "Préchargements", value: String(confirmation.counts?.preloads || 0) },
      ]}
      warningMessage="Les saisies non enregistrées seront aussi effacées. Les tonnages, tarifs exceptionnels et données d’exécution de Data Manager seront conservés."
      textPrompt={{ label: "Motif de la remise à zéro", required: true, minLength: 3 }}
      confirmLabel="Recommencer et rouvrir le Dispatch" isBusy={busy} errorMessage={error}
      onCancel={() => { if (!busy) setConfirmation(null); }} onConfirm={confirm} /> : null}
  </>;
}

export function DayWarnings({ day, children }) {
  const state = day.consolidation;
  if (!state) return children || null;
  return <>
    {/* Orange tant que les changements du Dispatch sont repliés, gris-bleu une fois ouverts. */}
    <div className={!state.editable ? "grid min-w-0 gap-2 rounded-[4px] border border-amber-200 border-l-4 border-l-[#c2410c] bg-amber-50 p-2 text-xs has-[[data-dispatch-open]]:border-[#b8c2ce] has-[[data-dispatch-open]]:border-l-[#c2410c] has-[[data-dispatch-open]]:bg-[#dce3ea]" : "contents"}>
    {!state.editable ? <p className="text-amber-900">
      {state.has_photo ? "Le Dispatch est ouvert. Vos saisies sont conservées en lecture seule. Clôturez le Dispatch après correction, puis examinez ses changements pour poursuivre."
        : "Clore la journée dans Dispatch avant de commencer Consolidation."}
    </p> : null}
    {children}
    </div>
    {state.legacy ? <p className="text-xs text-slate-500">Les relevés existants gardent leurs références historiques. Le remplacement de toute la journée se trouve dans Actions avancées.</p> : null}
  </>;
}
